import { listTasks } from "./tasks";
import type { TaskListParams, TaskRead } from "./types";

/** Calendar event = unified shape for anything we render on a day cell.
 *  V1 ships with task-typed events only — the activity API is
 *  per-company, no project-wide endpoint to aggregate from. The
 *  discriminator is in place so a follow-up commit can drop activities
 *  in without changing every consumer. */
export type CalendarEvent =
  | {
      type: "task";
      id: number;
      /** ISO timestamp the event sorts by — `due_at` for tasks. */
      at: string;
      task: TaskRead;
    };

interface CalendarFetchParams {
  /** Inclusive lower bound (UTC ISO). */
  start: string;
  /** Inclusive upper bound (UTC ISO). */
  end: string;
  /** When set, only "my tasks" — mirrors the Tasks page scope toggle. */
  assignedToMe?: boolean;
}

/** Fetch every calendar event in [start, end]. Bypasses pagination by
 *  asking for a generous page size — the calendar's window is at most
 *  a month + bordering weeks, so a few hundred items is the realistic
 *  ceiling. */
export async function getCalendarEvents(
  slug: string,
  params: CalendarFetchParams,
): Promise<CalendarEvent[]> {
  const baseParams: TaskListParams = {
    page_size: 500,
    due_after: params.start,
    due_before: params.end,
  };
  if (params.assignedToMe) baseParams.assigned_to = "me";
  // GET /tasks defaults to is_completed=false on some backends — pull
  // both halves so completed tasks still appear on their due date.
  const [openRes, doneRes] = await Promise.all([
    listTasks(slug, { ...baseParams, is_completed: false }),
    listTasks(slug, { ...baseParams, is_completed: true }),
  ]);
  const seen = new Set<number>();
  const events: CalendarEvent[] = [];
  for (const t of [...openRes.items, ...doneRes.items]) {
    if (!t.due_at) continue;
    if (seen.has(t.id)) continue;
    seen.add(t.id);
    events.push({ type: "task", id: t.id, at: t.due_at, task: t });
  }
  return events;
}
